import { useCallback, useRef } from 'react'

type AutoFocusHandler = (event: Event) => void

/**
 * Radix modal dialogs return focus to their `Trigger` on close. Most dialogs here are
 * controlled by state and opened from a plain button, so there is no Trigger and focus
 * would drop to `<body>`. This remembers the element that had focus when the dialog
 * opened and restores it on close; when a Trigger exists but is not the opener (or the
 * opener is gone) Radix's own behaviour stays in charge.
 */
export function useRestoreFocus(
  onOpenAutoFocus?: AutoFocusHandler,
  onCloseAutoFocus?: AutoFocusHandler,
) {
  const openerRef = useRef<HTMLElement | null>(null)

  const handleOpen = useCallback(
    (event: Event) => {
      const active = document.activeElement
      openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null
      onOpenAutoFocus?.(event)
    },
    [onOpenAutoFocus],
  )

  const handleClose = useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event)
      if (event.defaultPrevented) return
      const opener = openerRef.current
      openerRef.current = null
      if (opener?.isConnected) {
        event.preventDefault()
        opener.focus({ preventScroll: true })
        return
      }
      if (opener) {
        // The opener was removed (for example a deleted row): land on the page's main
        // region rather than <body> so keyboard users keep their place.
        const main = document.querySelector<HTMLElement>('main')
        if (main) {
          event.preventDefault()
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1')
          main.focus({ preventScroll: true })
        }
      }
    },
    [onCloseAutoFocus],
  )

  return { onOpenAutoFocus: handleOpen, onCloseAutoFocus: handleClose }
}
